import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCommunitySessionsHook } from '@/hooks/useCommunitySessionsHook';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import LoadingSpinner from '@/components/LoadingSpinner';
import { CommunitySessionCard } from '@/components/community/CommunitySessionCard';
import { Users, Search, Filter, Star, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const CommunitySessionsPage = () => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'featured'>('all');

  // Fetch all community sessions (not just featured)
  const { communitySessions, loading, error, refetch } = useCommunitySessionsHook(
    selectedCategory === 'all' ? undefined : selectedCategory,
    false // Don't filter to featured only
  );

  // Get categories for filter dropdown
  const { accessibleCategories } = useAccessibleCategories();

  // Filter sessions based on search term and tab
  const filteredSessions = useMemo(() => {
    let filtered = communitySessions;

    // Filter by search term
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(session =>
        session.title.toLowerCase().includes(lowercaseSearch) ||
        session.content.toLowerCase().includes(lowercaseSearch) ||
        session.tags?.some(tag => tag.toLowerCase().includes(lowercaseSearch)) ||
        session.submitted_by_name?.toLowerCase().includes(lowercaseSearch)
      );
    }

    // Filter by tab
    if (activeTab === 'featured') {
      filtered = filtered.filter(session => session.is_primary);
    }

    return filtered;
  }, [communitySessions, searchTerm, activeTab]);

  // Group sessions by category for the "By Category" tab
  const sessionsByCategory = useMemo(() => {
    return filteredSessions.reduce((acc, session) => {
      const category = session.category_id;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(session);
      return acc;
    }, {} as Record<string, typeof filteredSessions>);
  }, [filteredSessions]);

  // Stats
  const featuredCount = communitySessions.filter(s => s.is_primary).length;
  const totalCount = communitySessions.length;

  if (loading && communitySessions.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-2xl font-semibold">Failed to Load Community Sessions</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-lg bg-primary/10">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Community Sessions
            </h1>
            <p className="text-muted-foreground mt-1">
              Discover and explore meditations shared by our community members
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 flex-wrap">
          <Badge variant="secondary" className="text-sm py-2 px-4">
            {totalCount} Total Sessions
          </Badge>
          <Badge variant="default" className="text-sm py-2 px-4 bg-yellow-500 text-black">
            <Star className="h-4 w-4 mr-1" />
            {featuredCount} Featured
          </Badge>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sessions, tags, or contributors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="w-full md:w-64">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    <SelectValue placeholder="All Categories" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {accessibleCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || selectedCategory !== 'all') && (
            <div className="flex gap-2 mt-4 flex-wrap items-center">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchTerm && (
                <Badge variant="secondary" className="gap-1">
                  Search: {searchTerm}
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-1 hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {selectedCategory !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Category: {accessibleCategories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="ml-1 hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="h-6 text-xs"
              >
                Clear All
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'featured')} className="space-y-6">
        <TabsList className="grid w-full md:w-[400px] grid-cols-2">
          <TabsTrigger value="all" className="gap-2">
            <Users className="h-4 w-4" />
            All Sessions ({filteredSessions.length})
          </TabsTrigger>
          <TabsTrigger value="featured" className="gap-2">
            <Star className="h-4 w-4" />
            Featured ({filteredSessions.filter(s => s.is_primary).length})
          </TabsTrigger>
        </TabsList>

        {/* All Sessions Tab */}
        <TabsContent value="all" className="space-y-6">
          {filteredSessions.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Sessions Found</h3>
                <p className="text-muted-foreground text-center">
                  {searchTerm || selectedCategory !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'No community sessions available yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSessions.map((session) => (
                <CommunitySessionCard key={session.id} session={session} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Featured Sessions Tab */}
        <TabsContent value="featured" className="space-y-6">
          {filteredSessions.filter(s => s.is_primary).length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Star className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Featured Sessions</h3>
                <p className="text-muted-foreground text-center">
                  Featured sessions will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSessions
                .filter(s => s.is_primary)
                .map((session) => (
                  <CommunitySessionCard key={session.id} session={session} />
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* By Category View (Additional Section) */}
      {Object.keys(sessionsByCategory).length > 1 && activeTab === 'all' && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Browse by Category</h2>
          <div className="space-y-8">
            {Object.entries(sessionsByCategory).map(([categoryId, sessions]) => {
              const category = accessibleCategories.find(c => c.id === categoryId);
              return (
                <div key={categoryId}>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    {category?.name || categoryId}
                    <Badge variant="secondary">{sessions.length}</Badge>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sessions.slice(0, 3).map((session) => (
                      <CommunitySessionCard key={session.id} session={session} />
                    ))}
                  </div>
                  {sessions.length > 3 && (
                    <Button
                      variant="ghost"
                      className="mt-4"
                      onClick={() => setSelectedCategory(categoryId)}
                    >
                      View all {sessions.length} {category?.name || categoryId} sessions →
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunitySessionsPage;
